import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { getTranslation } from '../translations';
import { getInitialLanguage } from '../utils/languageDetection';
import PromoBanner from '../components/PromoBanner';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import Footer from '../components/Footer';
import './AuthPage.css';

export default function AuthPage({ setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [language, setLanguage] = useState(getInitialLanguage());
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [landingDraft, setLandingDraft] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    language: getInitialLanguage(),
    agbAccepted: false
  });
  
  // Check for referral code in URL or localStorage on mount
  useEffect(() => {
    const refFromUrl = searchParams.get('ref');
    const refFromStorage = localStorage.getItem('referral_code');
    
    if (refFromUrl) {
      setReferralCode(refFromUrl);
      localStorage.setItem('referral_code', refFromUrl);
      // Validate the referral code
      axios.get(`${API}/referral/validate/${refFromUrl}`)
        .then(res => {
          if (res.data.valid) {
            toast.success(language === 'de' 
              ? `Eingeladen von ${res.data.referrer_name}!` 
              : `Referred by ${res.data.referrer_name}!`);
          }
        })
        .catch(() => {});
    } else if (refFromStorage) {
      setReferralCode(refFromStorage);
    }
  }, [searchParams, language]);

  useEffect(() => {
    const savedDraft = localStorage.getItem('landingStoryDraft');
    if (!savedDraft) return;
    try {
      const parsedDraft = JSON.parse(savedDraft);
      if (parsedDraft?.child_name) {
        setLandingDraft(parsedDraft);
      }
    } catch (error) {
      console.error('Failed to parse landing draft', error);
    }
  }, []);
  
  const t = (key) => getTranslation(language, key);
  
  // Update language in localStorage and formData
  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    setFormData({...formData, language: newLanguage});
    localStorage.setItem('appLanguage', newLanguage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate AGB acceptance for registration
    if (!isLogin && !formData.agbAccepted) {
      toast.error(language === 'de' ? 'Sie müssen den Allgemeinen Geschäftsbedingungen zustimmen' : 'You must agree to the Terms and Conditions');
      return;
    }
    
    // Validate password confirmation for registration
    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        toast.error(language === 'de' ? 'Passwörter stimmen nicht überein' : 'Passwords do not match');
        return;
      }
      
      if (formData.password.length < 6) {
        toast.error(language === 'de' ? 'Passwort muss mindestens 6 Zeichen lang sein' : 'Password must be at least 6 characters');
        return;
      }
    }
    
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      // Don't send confirmPassword and agbAccepted to backend
      const { confirmPassword, agbAccepted, ...dataToSend } = formData;
      
      // Add referral code for registration
      if (!isLogin && referralCode) {
        dataToSend.referral_code = referralCode;
      }
      
      const response = await axios.post(`${API}${endpoint}`, dataToSend);
      
      localStorage.setItem('token', response.data.token);
      // Clear referral code from localStorage after successful registration
      if (!isLogin) {
        localStorage.removeItem('referral_code');
      }
      setUser(response.data.user);
      toast.success(isLogin ? t('toasts.welcomeBack') : t('toasts.accountCreated'));
      
      const hasLandingDraft = Boolean(localStorage.getItem('landingStoryDraft'));
      setTimeout(() => {
        navigate(hasLandingDraft ? '/create' : '/dashboard');
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.authenticationFailed'));
    }
  };

  const handleGoogleLogin = () => {
    const redirectPath = localStorage.getItem('landingStoryDraft') ? '/create' : '/dashboard';
    const redirectUrl = encodeURIComponent(`${window.location.origin}${redirectPath}`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
  };

  return (
    <div className="auth-page" style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      position: 'relative'
    }}>
      <PromoBanner language={language} onLanguageChange={handleLanguageChange} />

      {/* Main content - centered */}
      <div style={{ flex: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div className="auth-container">
          <div className="auth-header">
            <img 
              src="/storytales-logo.png" 
              alt="StoryTales Logo" 
              className="logo-image"
            />
            <p>{language === 'de' ? 'Erstelle magische personalisierte Geschichten für Kinder' : 'Create magical personalized stories for children'}</p>
          </div>

          {landingDraft && (
            <div className="auth-draft-preview">
              <span className="auth-draft-kicker">
                {language === 'de' ? 'Dein Entwurf wartet' : 'Your draft is ready'}
              </span>
              <h2>
                {language === 'de'
                  ? `Wir haben ${landingDraft.child_name}s erste Geschichte vorbereitet`
                  : `We prepared ${landingDraft.child_name}'s first story`}
              </h2>
              <div className="auth-draft-chips">
                <span>{language === 'de' ? `Name: ${landingDraft.child_name}` : `Name: ${landingDraft.child_name}`}</span>
                <span>{language === 'de' ? `Alter: ${landingDraft.age}` : `Age: ${landingDraft.age}`}</span>
                <span>{language === 'de' ? `Thema: ${landingDraft.theme_label || landingDraft.theme}` : `Theme: ${landingDraft.theme_label || landingDraft.theme}`}</span>
              </div>
              <p>
                {language === 'de'
                  ? 'Melde dich an und wir übernehmen diesen Entwurf direkt in die Story-Erstellung.'
                  : 'Sign up or log in and we will carry this draft directly into story creation.'}
              </p>
            </div>
          )}
          
          <div className="auth-card">
            <div className="auth-tabs">
              <button 
                data-testid="login-tab"
                className={isLogin ? 'active' : ''} 
                onClick={() => {
                  setIsLogin(true);
                  setFormData({...formData, confirmPassword: ''});
                }}
              >
                {language === 'de' ? 'Anmelden' : 'Login'}
              </button>
              <button 
                data-testid="register-tab"
                className={isLogin ? '' : 'active'} 
                onClick={() => {
                  setIsLogin(false);
                  setFormData({...formData, confirmPassword: ''});
                }}
              >
                {language === 'de' ? 'Registrieren' : 'Sign Up'}
              </button>
            </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {!isLogin && (
              <>
                <div className="form-group">
                  <Label htmlFor="name">{language === 'de' ? 'Name' : 'Name'}</Label>
                  <Input
                    id="name"
                    data-testid="auth-name-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required={!isLogin}
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="language">Language / Sprache</Label>
                  <Select 
                    value={formData.language} 
                    onValueChange={(value) => setFormData({...formData, language: value})}
                  >
                    <SelectTrigger data-testid="language-select">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">🇬🇧 English</SelectItem>
                      <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="form-group">
              <Label htmlFor="email">{language === 'de' ? 'E-Mail' : 'Email'}</Label>
              <Input
                id="email"
                data-testid="auth-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <Label htmlFor="password">{language === 'de' ? 'Passwort' : 'Password'}</Label>
              <div style={{ position: 'relative' }}>
                <Input
                  id="password"
                  data-testid="auth-password-input"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  minLength={6}
                  placeholder={isLogin ? '' : (language === 'de' ? 'Mindestens 6 Zeichen' : 'At least 6 characters')}
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#666',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {!isLogin && formData.password && (
                <PasswordStrengthIndicator password={formData.password} language={language} />
              )}
              {isLogin && (
                <div style={{ textAlign: 'right', marginTop: '8px' }}>
                  <Button 
                    variant="link" 
                    onClick={() => window.location.href = '/forgot-password'}
                    style={{ padding: 0, height: 'auto', fontSize: '14px', color: '#0f766e' }}
                  >
                    {language === 'de' ? 'Passwort vergessen?' : 'Forgot password?'}
                  </Button>
                </div>
              )}
            </div>

            {!isLogin && (
              <div className="form-group">
                <Label htmlFor="confirmPassword">
                  {language === 'de' ? 'Passwort bestätigen' : 'Confirm Password'}
                </Label>
                <div style={{ position: 'relative' }}>
                  <Input
                    id="confirmPassword"
                    data-testid="auth-confirm-password-input"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    required={!isLogin}
                    minLength={6}
                    placeholder={language === 'de' ? 'Passwort erneut eingeben' : 'Re-enter your password'}
                    style={{ paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '18px',
                      color: '#666',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>
            )}

            {!isLogin && (
              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '20px' }}>
                  <input
                    id="agbAccepted"
                    type="checkbox"
                    checked={formData.agbAccepted || false}
                    onChange={(e) => setFormData({...formData, agbAccepted: e.target.checked})}
                    required
                    style={{ marginTop: '4px', flexShrink: 0 }}
                  />
                  <Label htmlFor="agbAccepted" style={{ fontSize: '14px', lineHeight: '1.4', cursor: 'pointer' }}>
                    {language === 'de' ? (
                      <>Ich stimme den <a href="/agb" target="_blank" rel="noopener noreferrer" style={{ color: '#0f766e', textDecoration: 'underline' }}>Allgemeinen Geschäftsbedingungen</a> und der <a href="/datenschutz" target="_blank" rel="noopener noreferrer" style={{ color: '#0f766e', textDecoration: 'underline' }}>Datenschutzerklärung</a> zu.</>
                    ) : (
                      <>I agree to the <a href="/agb" target="_blank" rel="noopener noreferrer" style={{ color: '#0f766e', textDecoration: 'underline' }}>Terms and Conditions</a> and <a href="/datenschutz" target="_blank" rel="noopener noreferrer" style={{ color: '#0f766e', textDecoration: 'underline' }}>Privacy Policy</a>.</>
                    )}
                  </Label>
                </div>
              </div>
            )}

            <Button data-testid="auth-submit-button" type="submit" className="auth-button">
              {isLogin
                ? (landingDraft
                    ? (language === 'de' ? 'Anmelden und Geschichte fortsetzen' : 'Log in and continue story')
                    : (language === 'de' ? 'Anmelden' : 'Login'))
                : (landingDraft
                    ? (language === 'de' ? 'Konto erstellen und Geschichte starten' : 'Create account and start story')
                    : (language === 'de' ? 'Konto erstellen' : 'Create Account'))}
            </Button>
          </form>

          <div className="divider">
            <span>{language === 'de' ? 'oder' : 'or'}</span>
          </div>

          <Button 
            data-testid="google-auth-button"
            onClick={handleGoogleLogin} 
            variant="outline" 
            className="google-button"
          >
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {language === 'de' ? 'Mit Google fortfahren' : 'Continue with Google'}
          </Button>
        </div>
      </div>
      </div>
      
      {/* Footer - Sticky at bottom */}
      <Footer language={language} />
    </div>
  );
}
